// content.js - Articles. They live as markdown files in src/content/articles so
// they are version controlled, render as real HTML at build time, and cost the
// site nothing at runtime. No CMS, no database read, no loading spinner.
//
// Each file starts with a small front matter block:
//
//   ---
//   title: What scrap gold is actually worth
//   description: One sentence for search results.
//   date: 2026-02-11
//   category: Pricing
//   readMinutes: 6
//   ---
//
// LEGEND
//   1. parseFrontMatter()  the tiny YAML subset we need, shared with lib/legal.js
//   2. ARTICLES            every article, newest first
//   3. getArticle()        one article with its rendered HTML
//   4. related()           three others worth reading

import { marked } from "marked";

////////// 1. FRONT MATTER //////////
// Deliberately not a YAML parser. Keys are flat strings; that is all we use.
// Exported because lib/legal.js reads the same block out of the legal documents.

export function parseFrontMatter(raw) {
  const match = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?([\s\S]*)$/.exec(raw);
  if (!match) return { meta: {}, body: raw };

  const meta = {};
  for (const line of match[1].split(/\r?\n/)) {
    const separator = line.indexOf(":");
    if (separator === -1) continue;
    const key = line.slice(0, separator).trim();
    let value = line.slice(separator + 1).trim();
    if ((value.startsWith('"') && value.endsWith('"')) || (value.startsWith("'") && value.endsWith("'"))) {
      value = value.slice(1, -1);
    }
    meta[key] = value;
  }
  return { meta, body: match[2] };
}

////////// 2. THE INDEX //////////
// Vite inlines every match at build time, so this is a static array in the bundle
// rather than a directory read.

const files = import.meta.glob("../content/articles/*.md", { eager: true, query: "?raw", import: "default" });

marked.setOptions({ gfm: true, breaks: false });

export const ARTICLES = Object.entries(files)
  .map(([path, raw]) => {
    const slug = path.split("/").pop().replace(/\.md$/, "");
    const { meta, body } = parseFrontMatter(raw);
    const words = body.split(/\s+/).length;
    return {
      slug,
      title: meta.title || slug,
      description: meta.description || "",
      date: meta.date || "",
      updated: meta.updated || meta.date || "",
      category: meta.category || "Guide",
      featured: meta.featured === "true",
      readMinutes: Number(meta.readMinutes) || Math.max(2, Math.round(words / 220)),
      // Carried through to Article schema. A search engine reads it as a signal
      // that a page is a real piece of writing rather than a stub.
      wordCount: words,
      body,
    };
  })
  .sort((a, b) => String(b.date).localeCompare(String(a.date)));

export const ARTICLE_SLUGS = ARTICLES.map((article) => article.slug);

export const CATEGORIES = [...new Set(ARTICLES.map((article) => article.category))].sort();

////////// 3. ONE ARTICLE //////////

export function getArticle(slug) {
  const article = ARTICLES.find((entry) => entry.slug === slug);
  if (!article) return null;
  return { ...article, html: marked.parse(article.body) };
}

////////// 4. RELATED //////////
// Same category first, then the newest of anything else. Never the article itself.

export function related(slug, count = 3) {
  const current = ARTICLES.find((entry) => entry.slug === slug);
  if (!current) return ARTICLES.slice(0, count);

  const sameCategory = ARTICLES.filter((entry) => entry.slug !== slug && entry.category === current.category);
  const rest = ARTICLES.filter((entry) => entry.slug !== slug && entry.category !== current.category);
  return [...sameCategory, ...rest].slice(0, count);
}
